import { inject, Injectable, signal } from '@angular/core';
import { from, map, Observable, Subscription, switchMap, tap, timer } from 'rxjs';

import { LicenseModel } from '~app/models/license.model';
import { TicketListModel } from '~app/models/responses/ticket-list.model';
import { TicketViewerModel } from '~app/models/responses/ticket-viewer.model';
import { TicketModel } from '~app/models/ticket.model';
import { LicensesStore } from '~app/services/stores/licenses.store';
import { TicketsStore } from '~app/services/stores/tickets.store';
import { TicketHeaderTitleAdditionalInfoComponent } from '~app/views/support/tickets/components/ticket-header-title-additional-info/ticket-header-title-additional-info.component';
import { RightSidebarConfigModel } from '~core/models/right-sidebar-config.model';
import { OptionalType } from '~shared/core/types/optional.type';
import { SidebarHeaderService } from './sidebar-header.service';
import { SidebarService } from './sidebar.service';
import { TranslationService } from './translation.service';

@Injectable({
    providedIn: 'root'
})
export class TicketService {
    readonly ticket = signal<OptionalType<TicketModel>>(undefined);
    readonly license = signal<OptionalType<LicenseModel>>(undefined);
    readonly licenseTickets = signal<OptionalType<TicketListModel[]>>([]);
    readonly isLoading = signal<boolean>(true);
    readonly ticketViewers = signal<TicketViewerModel[]>([]);

    private ticketTimeIntervalSubscription = new Subscription();

    private readonly sidebarService = inject(SidebarService);
    private readonly ticketsStore = inject(TicketsStore);
    private readonly licenseStore = inject(LicensesStore);
    private readonly translationService = inject(TranslationService);
    private readonly headerService = inject(SidebarHeaderService);

    editTicket(config: RightSidebarConfigModel): Observable<OptionalType<boolean>> {
        this.isLoading.set(true);
        this.ticketsStore
            .get(config.data)
            .pipe(
                tap(result => {
                    if (!result.value) {
                        return;
                    }
                    this.licenseStore.get(result.value.licenseId).subscribe({
                        next: license => {
                            this.license.set(license.value);
                        }
                    });
                }),
                tap(result => {
                    if (!result.value) {
                        return;
                    }
                    this.licenseStore.getLicenseTickets(result.value.licenseId, result.value.id).subscribe({
                        next: license => {
                            this.licenseTickets.set(license.value);
                        }
                    });
                }),
                switchMap(result => {
                    return from(
                        import('~app/views/support/tickets/components/ticket-header-additional-info/ticket-header-additional-info.component')
                    ).pipe(
                        map(({ TicketHeaderAdditionalInfoComponent }) => {
                            return { result, TicketHeaderAdditionalInfoComponent };
                        })
                    );
                })
            )
            .subscribe({
                next: ({ result, TicketHeaderAdditionalInfoComponent }) => {
                    const ticket = result.value;
                    this.ticket.set(ticket);

                    if (!!ticket) {
                        this.ticketTimeIntervalSubscription = timer(0, 10000).subscribe(() => {
                            this.ticketsStore.updateTicketTime(ticket.id).subscribe(r => {
                                this.ticketViewers.set(r.value ?? []);
                            });
                        });
                    }

                    const headerText = this.translationService.getText('Tickets.Edit.Ticket') + ` #${ticket?.id}`;
                    this.headerService.title.set(headerText);
                    this.headerService.hasCloseButton.set(false);
                    this.headerService.item.set(ticket);
                    this.headerService.additionalInfoComponent.set(TicketHeaderAdditionalInfoComponent);
                    this.headerService.titleAdditionalComponent.set(TicketHeaderTitleAdditionalInfoComponent);
                    this.isLoading.set(false);
                }
            });
        return this.sidebarService.openRight<undefined>(config).pipe(
            tap(() => {
                this.resetTicket();
            })
        );
    }

    createTicket(config: RightSidebarConfigModel): Observable<OptionalType<boolean>> {
        const headerText = this.translationService.getText('Tickets.NewEntity');
        this.headerService.title.set(headerText);
        this.headerService.hasCloseButton.set(false);

        return this.sidebarService.openRight<undefined>(config).pipe(
            tap(() => {
                this.resetTicket();
            })
        );
    }

    resetTicket(): void {
        this.ticket.set(undefined);
        this.license.set(undefined);
        this.licenseTickets.set(undefined);
        this.headerService.reset();
        this.ticketTimeIntervalSubscription.unsubscribe();
    }

    reloadTicket(): void {
        if (this.ticket()?.id) {
            this.isLoading.set(true);
            this.ticketsStore.get(this.ticket()!.id).subscribe(response => {
                this.ticket.set(response.value);
                this.isLoading.set(false);
            });
        }
    }
}
